/**
 * Dashboard customization (which widgets show, in what order, and whether the
 * dashboard is the default landing page). Persisted in localStorage.
 */

export const DASHBOARD_WIDGETS = [
  { id: "stats", label: "Stat cards" },
  { id: "velocity", label: "Pricing velocity" },
  { id: "categories", label: "Categories chart" },
  { id: "diff", label: "Changes since last snapshot" },
  { id: "activity", label: "Recent activity" },
] as const;

export type WidgetId = (typeof DASHBOARD_WIDGETS)[number]["id"];
const ALL_IDS: WidgetId[] = DASHBOARD_WIDGETS.map((w) => w.id);

export interface DashboardSettings {
  order: WidgetId[];
  hidden: WidgetId[];
  defaultLanding: "dashboard" | "profiles";
}

export const DEFAULT_DASHBOARD_SETTINGS: DashboardSettings = {
  order: ALL_IDS,
  hidden: [],
  defaultLanding: "dashboard",
};

const STORAGE_KEY = "catalogkit.dashboardSettings";

function isWidgetId(value: unknown): value is WidgetId {
  return typeof value === "string" && (ALL_IDS as string[]).includes(value);
}

export function loadDashboardSettings(): DashboardSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return { ...DEFAULT_DASHBOARD_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<DashboardSettings>;
    const order = Array.isArray(parsed.order) ? parsed.order.filter(isWidgetId) : [];
    // Append any widgets missing from the stored order (e.g. new widgets).
    for (const id of ALL_IDS) if (!order.includes(id)) order.push(id);
    const hidden = Array.isArray(parsed.hidden) ? parsed.hidden.filter(isWidgetId) : [];
    const defaultLanding = parsed.defaultLanding === "profiles" ? "profiles" : "dashboard";
    return { order, hidden, defaultLanding };
  } catch {
    return { ...DEFAULT_DASHBOARD_SETTINGS };
  }
}

export function saveDashboardSettings(settings: DashboardSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage unavailable — settings revert to defaults next load.
  }
}
